import React, { useState, useMemo, useEffect } from "react";
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { PolicyOrAccount } from "../../../interfaces/DTO/mislaka_xml_models";
import { colorContributionType } from "../../../utils/colorEnumFormatter";
import { formatContributionType, formatDepositorType, formatDepositTypeCode } from "../../../utils/enumFormatter";
import { formatCurrency, formatIsoDate } from "../../../utils/namesFormater";
import { MimshakService } from "../../../services/mimshakService";
import { MimshakGroupByService } from "../../../services/MimshakGroupByService";
 // ייבוא הפונקציות הסטטיות

interface DepositsBarChartByContributionTypeProps {
  policies: PolicyOrAccount[];
}

const DepositsBarChartByContributionType: React.FC<DepositsBarChartByContributionTypeProps> = ({ policies }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [selectedDateField, setSelectedDateField] = useState<"payment_period" | "salary_month">("payment_period");

  const latestUpdateDate = useMemo(() => MimshakService.getLatestUpdateDate(policies), [policies]);

  const aggregatedDeposits = useMemo(() => {
    if (!latestUpdateDate) return {};
    return MimshakGroupByService.groupByDepositsByPeriodAndCategoryTypeFillingEmptyLast12(
      policies,
      latestUpdateDate,
      selectedDateField
    );
  }, [policies, latestUpdateDate, selectedDateField]);

  const uniquePeriods = useMemo(() => Object.keys(aggregatedDeposits).map(Number).sort((a, b) => a - b), [aggregatedDeposits]);

  const hasDeposits = useMemo(() => {
    return Object.values(aggregatedDeposits).some(period =>
      Object.values(period).some(amount => amount > 0)
    );
  }, [aggregatedDeposits]);

  useEffect(() => {
    if (uniquePeriods.length > 0) {
      const lastNonZeroPeriod = [...uniquePeriods].reverse().find(period =>
        Object.values(aggregatedDeposits[period] || {}).some(amount => amount > 0)
      );
      setSelectedPeriod(lastNonZeroPeriod ?? uniquePeriods[uniquePeriods.length - 1]);
    }
  }, [uniquePeriods, aggregatedDeposits]);

  if (!hasDeposits) return null;

  const chartData = uniquePeriods.map(period => {
    const periodData: any = { period: period.toString() };
    Object.entries(aggregatedDeposits[period] || {}).forEach(([contributionType, amount]) => {
      periodData[contributionType] = amount;
    });
    return periodData;
  });

  const handleBarClick = (data: any) => setSelectedPeriod(Number(data.period));

  const formatLegend = (value: string) => formatContributionType(Number(value));
  const formatTooltip = (value: any, name: string) => [formatCurrency(value), formatContributionType(Number(name))];

  const allContributionTypes = Array.from(new Set(chartData.flatMap(period => Object.keys(period)))).filter(key => key !== "period");

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        התפלגות הפקדות לפי סוג תרומה ({selectedDateField === "payment_period" ? "לפי תקופת תשלום" : "לפי חודש שכר"})
      </Typography>

   

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} margin={{ top: 40, right: 30, left: 50, bottom: 5 }}
                  onClick={(e) => e && e.activeLabel && setSelectedPeriod(Number(e.activeLabel))}>
          <XAxis dataKey="period" tickFormatter={(value) => `${value.toString().slice(4, 6)}-${value.toString().slice(0, 4)}`} />
          <YAxis tickFormatter={formatCurrency} domain={["auto", "auto"]} dx={-70} />
          <Tooltip formatter={formatTooltip} labelFormatter={(label) => `${label.toString().slice(4, 6)}-${label.toString().slice(0, 4)}`} />
          <Legend verticalAlign="top" align="center" formatter={formatLegend} wrapperStyle={{ marginBottom: 10 }} />
          {allContributionTypes.map(contributionType => (
            <Bar key={contributionType} dataKey={contributionType} stackId="a" fill={colorContributionType(Number(contributionType))} onClick={handleBarClick} />
          ))}
        </BarChart>
      </ResponsiveContainer>

      {selectedPeriod && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 0 }}>
            פרטי הפקדות עבור {selectedDateField === "payment_period" ? "תקופת תשלום" : "חודש שכר"} {selectedPeriod}
               <ToggleButtonGroup
        value={selectedDateField}
        exclusive
        onChange={(e, newValue) => newValue && setSelectedDateField(newValue)}
        sx={{ mb: 0, mr:2,}}
      >
        <ToggleButton value="payment_period">תקופת תשלום</ToggleButton>
        <ToggleButton value="salary_month">חודש שכר</ToggleButton>
      </ToggleButtonGroup>
          </Typography>
          
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>תאריך הפקדה</TableCell>
                  <TableCell>סוג הפקדה</TableCell>
                  <TableCell>סוג תרומה</TableCell>
                  <TableCell>סוג מפקיד</TableCell>
                  <TableCell>סכום הפקדה</TableCell>
                  <TableCell>בסיס שכר</TableCell>
                  <TableCell>חודש שכר</TableCell>
                  <TableCell>תקופת תשלום</TableCell>
                  <TableCell>שם המעסיק</TableCell>
                  <TableCell>אחוז הפרשה</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const filtered = MimshakService.getDepositsForLast12Months(policies, latestUpdateDate)
                    .filter(d => String(d[selectedDateField]) === String(selectedPeriod));

                  // לכל employer+salaryMonth, לכל contribution_type: צוברים effective ו-reported
                  // ומציגים merged effective רק אם הסכום שונה מהמדווח
                  const effectiveSum: Record<string, Record<number, number>> = {};
                  const reportedSum: Record<string, Record<number, number>> = {};
                  filtered.forEach(d => {
                    const key = `${d.employer_name || ""}__${d.salary_month || ""}`;
                    const ct = d.contribution_type as number;
                    const rate = d.deposit_rate;
                    const paid = d.deposit_amount_paid;
                    const absPaid = Math.abs(paid);
                    const reported = d.salary_base || 0;
                    if (!effectiveSum[key]) effectiveSum[key] = {};
                    if (!reportedSum[key]) reportedSum[key] = {};
                    // effective: מ-rate אם קיים, אחרת מ-salary_base
                    let derived: number;
                    if (rate != null && rate > 0 && absPaid > 0) {
                      derived = absPaid / (rate / 100);
                    } else {
                      derived = reported;
                    }
                    effectiveSum[key][ct] = (effectiveSum[key][ct] || 0) + (paid >= 0 ? derived : -derived);
                    reportedSum[key][ct] = (reportedSum[key][ct] || 0) + reported;
                  });
                  // mergedEffectiveSalaryBase: רק כשהסכום שונה מהמדווח
                  const mergedEffectiveSalaryBase: Record<string, Record<number, number>> = {};
                  Object.entries(effectiveSum).forEach(([key, byType]) => {
                    Object.entries(byType).forEach(([ct, eff]) => {
                      const rep = (reportedSum[key] || {})[Number(ct)] || 0;
                      if (rep > 0 && Math.abs(eff - rep) / rep > 0.01 || rep === 0 && eff !== 0) {
                        if (!mergedEffectiveSalaryBase[key]) mergedEffectiveSalaryBase[key] = {};
                        mergedEffectiveSalaryBase[key][Number(ct)] = eff;
                      }
                    });
                  });

                  const shownGroups = new Set<string>();
                  return filtered.map((deposit, index) => {
                    const key = `${deposit.employer_name || ""}__${deposit.salary_month || ""}`;
                    const isFirstInGroup = !shownGroups.has(key);
                    if (isFirstInGroup) shownGroups.add(key);
                    const derivedByType = mergedEffectiveSalaryBase[key] || {};


                    const reported = deposit.deposit_rate != null ? deposit.deposit_rate : null;
                    const absPaid = Math.abs(deposit.deposit_amount_paid);
                    const reportedBase = deposit.salary_base || 0;
                    const calculatedRate = reportedBase > 0 ? Math.round((absPaid / reportedBase) * 1000) / 10 : null;
                    const reportedRateRounded = reported != null ? Math.round(reported * 10) / 10 : null;
                    const isDifferent = calculatedRate != null && (reported == null || reportedRateRounded !== calculatedRate);

                    return (
                      <React.Fragment key={index}>
                        <TableRow>
                          <TableCell>{formatIsoDate(deposit.deposit_date)}</TableCell>
                          <TableCell>{formatDepositTypeCode(deposit.deposit_type_code)}</TableCell>
                          <TableCell>{formatContributionType(deposit.contribution_type)}</TableCell>
                          <TableCell>{formatDepositorType(deposit.depositor_type)}</TableCell>
                          <TableCell>{formatCurrency(deposit.deposit_amount_paid)}</TableCell>
                          <TableCell>
                            <>
                              {formatCurrency(deposit.salary_base)}
                              {isDifferent && reported != null && reported > 0 && (
                                <Typography component="span" sx={{ color: "blue", display: "block", fontSize: "0.75rem" }}>
                                  מחושב: {formatCurrency(deposit.deposit_amount_paid / (reported / 100))}
                                </Typography>
                              )}
                              {derivedByType[deposit.contribution_type as number] != null && (
                                <Typography component="span" sx={{ color: "blue", display: "block", fontSize: "0.75rem", mt: 0.5 }}>
                                  סה"כ מחושב: {formatCurrency(derivedByType[deposit.contribution_type as number])}
                                </Typography>
                              )}
                            </>
                          </TableCell>
                          <TableCell>{deposit.salary_month}</TableCell>
                          <TableCell>{deposit.payment_period}</TableCell>
                          <TableCell>{deposit.employer_name || "-"}</TableCell>
                          <TableCell>
                            <>
                              {reported != null ? `${reported.toFixed(2)}%` : "-"}
                              {isDifferent && calculatedRate != null && (
                                <Typography component="span" sx={{ color: calculatedRate < 0 ? "red" : "blue", display: "block", fontSize: "0.75rem" }}>
                                  בפועל: <span dir="ltr">{calculatedRate.toFixed(1)}%</span>
                                </Typography>
                              )}
                            </>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  });
                })()}
                <TableRow sx={{ fontWeight: "bold", backgroundColor: "#f0f0f0" }}>
                  <TableCell colSpan={4}>סה"כ הפקדות</TableCell>
                  <TableCell>
                    {formatCurrency(
                      MimshakService.getDepositsForLast12Months(policies, latestUpdateDate)
                        .filter(d => String(d[selectedDateField]) === String(selectedPeriod))
                        .reduce((sum, deposit) => sum + deposit.deposit_amount_paid, 0)
                    )}
                  </TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default DepositsBarChartByContributionType;
